/** Keep app call tones and direct notifications quiet while Live is open.
 * Вимикаємо сигнали викликів і звук прямих сповіщень під час живої розмови. */
let active = false;
const listeners = new Set<() => void>();
const pendingWork = new Set<object>();

export function setLiveVoiceActive(value: boolean): void {
  if (active === value) return;
  active = value;
  for (const listener of listeners) listener();
}

export function isLiveVoiceActive(): boolean {
  return active;
}

/** Prevent automatic reload until voice input has finished draining or being delivered.
 * Не дозволяє автоматичне перезавантаження до завершення дочитування чи доставлення вводу. */
export function holdLiveVoiceWork(): () => void {
  const work = {};
  pendingWork.add(work);
  return () => {
    pendingWork.delete(work);
  };
}

export function hasLiveVoiceWork(): boolean {
  return active || pendingWork.size > 0;
}

export function subscribeLiveVoiceActive(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The service worker asks this before showing a notice, so a reply arriving during Live
 * does not chime over the conversation. It must match `LIVE_VOICE_QUERY` in `public/sw.js`.
 * Сервіс-воркер питає це перед сповіщенням, щоб відповідь під час живої розмови не дзвеніла. */
export const LIVE_VOICE_QUERY = "HROKBOT_LIVE_VOICE_QUERY";

export type LiveVoiceQueryEvent = {
  data: unknown;
  ports: readonly { postMessage(message: unknown): void }[];
};

type LiveVoiceQuerySource = {
  addEventListener(type: "message", listener: (event: LiveVoiceQueryEvent) => void): void;
  startMessages?(): void;
};

/** Answer the service worker's question on the port it sent. / Відповідаємо на питання воркера. */
export function answerLiveVoiceQueries(container: LiveVoiceQuerySource): void {
  container.addEventListener("message", (event) => {
    const data = event.data;
    const asked =
      typeof data === "object" && data !== null && "type" in data && data.type === LIVE_VOICE_QUERY;
    if (asked) event.ports[0]?.postMessage({ liveVoice: active });
  });
  // A listener added with addEventListener does not open the worker's message queue by itself.
  container.startMessages?.();
}
